
import { defineComponent, nextTick } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { settingsdata } from "@/api/settings";
import {
  createContentCampaign,
  getContentCampaign,
  getContentSettings,
  updateContentCampaign
} from "@/api/ghlContent.api.js";
import {
  createReelJob,
  generateReelScript,
  getReelJob,
  getReelVoices
} from "@/api/ghlAi.api.js";
import { fetchGhlPaymentCatalogList } from "@/api/ghlPayment.api.js";
import { getActiveInstance } from "@/utils/ghlInstance.js";
import { isFacelessReelPreviewUser } from "@/utils/facelessReelPreview.js";
import { REEL_TYPE_OPTIONS, getReelTypeOption } from "@/utils/reelTypeCatalog.js";
import { filterReelEligibleProducts } from "@/utils/reelProductCatalog.js";
import SocialPostPreview from "@/components/social/SocialPostPreview.vue";
import ContentMediaLibrary from "@/components/social/ContentMediaLibrary.vue";
import ReelCatalogLibrary from "@/components/social/ReelCatalogLibrary.vue";
import ReelSceneEditor from "@/components/social/ReelSceneEditor.vue";
import ReelVoicePicker from "@/components/social/ReelVoicePicker.vue";
import SuggestCaptionDialog from "@/components/social/SuggestCaptionDialog.vue";
import SuggestPostImageDialog from "@/components/social/SuggestPostImageDialog.vue";
import {
  accountDisplayName,
  accountOptionKey,
  getPlatformMeta,
  normalizePlatform,
  uniquePlatformsFromAccounts
} from "@/utils/socialPlatformMeta.js";
import {
  MAX_FOLLOW_UP_COMMENT_LENGTH,
  contentHasVideo,
  facebookPostTypeRequiresVideo,
  hasPendingVideoFiles,
  isImageMediaUrl,
  isVideoMediaUrl,
  keepSingleVideoMediaUrl,
  stripVideoMediaUrls
} from "@/utils/socialPostPolicy.js";

const EDITABLE_CAMPAIGN_STATUSES = new Set(["draft", "preparing", "rejected"]);

function getApiErrorMessage(error: unknown) {
  const candidate = error as { response?: { data?: { message?: unknown } } };
  return String(candidate?.response?.data?.message || "");
}

type PendingFile = {
  file: File;
  previewUrl: string;
  isImage: boolean;
};

export default defineComponent({
  name: "team-content-publish",
  components: {
    SocialPostPreview,
    ContentMediaLibrary,
    ReelCatalogLibrary,
    ReelSceneEditor,
    ReelVoicePicker,
    SuggestCaptionDialog,
    SuggestPostImageDialog
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: Number,
      default: 0
    }
  },
  emits: ["view-history", "new-post"],
  data() {
    return {
      loading: true,
      publishing: false,
      saving: false,
      isTeamLeader: false,
      hasGhlLocation: false,
      ghlLocationId: "",
      connectedAccounts: [] as any[],
      selectedAccountKeys: [] as string[],
      mediaUrlsText: "",
      scheduleDialogVisible: false,
      suggestCaptionVisible: false,
      suggestCaptionMode: "draft" as "draft" | "rewrite",
      suggestPostImageVisible: false,
      editingCampaignId: 0,
      campaignStatus: "",
      pendingFiles: [] as PendingFile[],
      attachedMediaIds: [] as number[],
      attachedPreviewUrls: [] as string[],
      pollTimer: null as ReturnType<typeof setInterval> | null,
      reelPollTimer: null as ReturnType<typeof setInterval> | null,
      facebookOptionsOpen: true,
      maxFollowUpCommentLength: MAX_FOLLOW_UP_COMMENT_LENGTH,
      facebookPostTypeOptions: [
        { value: "post", label: "Feed" },
        { value: "reel", label: "Reel" },
        { value: "story", label: "Story" }
      ],
      form: {
        caption: "",
        linkUrl: "",
        followUpComment: "",
        facebookPostType: "post",
        platforms: [] as string[],
        scheduledAt: "",
        cascadeToTeam: false,
        varyCaption: false
      },
      lastResult: null as any,
      reelVoices: [] as any[],
      reelTones: [
        { id: "educator", label: "Warm educator" },
        { id: "upbeat", label: "Bright & upbeat" },
        { id: "professional", label: "Professional" }
      ] as any[],
      reelVoiceId: "warm-female",
      reelToneId: "educator",
      reelPreviewBase: "https://images.empoweredconsumerism-static-files.com/reel-voices/v2",
      reelProductSku: "",
      reelType: "product",
      reelCommunityMediaIds: [] as number[],
      reelCatalogProducts: [] as any[],
      reelCatalogLoading: false,
      reelBrief: "",
      reelVoiceover: "",
      reelScriptJson: null as any,
      reelScriptLoading: false,
      reelGenerating: false,
      reelJobId: 0,
      reelJobError: "",
      reelPreviewAudio: null as HTMLAudioElement | null,
      reelVoicesExpanded: false,
      reelTonesExpanded: false,
      reelPanelExpanded: false
    };
  },
  computed: {
    pageTitle(): string {
      return this.editingCampaignId && this.isCampaignStatusEditable(this.campaignStatus)
        ? "Edit Draft"
        : "New Social Post";
    },
    showStartNewPostButton(): boolean {
      return Boolean(
        this.editingCampaignId
        || this.reelJobId
        || this.reelBrief.trim()
        || this.reelVoiceover.trim()
        || this.form.caption.trim()
        || this.pendingFiles.length
        || this.attachedMediaIds.length
      );
    },
    selectedAccounts(): any[] {
      return this.connectedAccounts.filter((account) =>
        this.selectedAccountKeys.includes(accountOptionKey(account))
      );
    },
    mediaUrls(): string[] {
      return this.mediaUrlsText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    },
    previewMediaUrls(): string[] {
      const pending = this.pendingFiles.map((item) => item.previewUrl).filter(Boolean);
      return [...this.attachedPreviewUrls, ...pending, ...this.mediaUrls];
    },
    previewMediaItems(): Array<{ url: string; isVideo: boolean }> {
      const pending = this.pendingFiles
        .filter((item) => item.previewUrl)
        .map((item) => ({
          url: item.previewUrl,
          isVideo: !item.isImage
        }));
      const attached = this.attachedPreviewUrls
        .filter(Boolean)
        .map((url) => ({
          url,
          isVideo: isVideoMediaUrl(url)
        }));
      const typed = this.mediaUrls.map((url) => ({
        url,
        isVideo: isVideoMediaUrl(url)
      }));
      return [...attached, ...pending, ...typed].filter((item, index, list) => (
        list.findIndex((entry) => entry.url === item.url) === index
      ));
    },
    footerDisabled(): boolean {
      return !this.hasGhlLocation || !this.selectedAccountKeys.length || this.publishing || this.saving || this.campaignStatus === "preparing";
    },
    postDisabled(): boolean {
      if (this.footerDisabled) return true;
      if (
        this.hasFacebookSelected
        && this.form.facebookPostType === "reel"
        && !this.previewHasVideo
      ) {
        return true;
      }
      if (this.reelGenerating) return true;
      return false;
    },
    reelTypeOptions() {
      return REEL_TYPE_OPTIONS;
    },
    reelTypeHelp(): string {
      return getReelTypeOption(this.reelType).description;
    },
    reelPanelIntro(): string {
      if (this.reelType === "business") {
        return "Auto-generate a new vertical video with AI voiceover. Select community clips, write a brief about your team, generate a script, then create your video.";
      }
      return "Auto-generate a new vertical video with AI voiceover. Pick a reel type, select a product, optionally add a brief, generate a script, then create your video.";
    },
    reelBriefPlaceholder(): string {
      if (this.reelType === "business") {
        return "Introduce your community and invite viewers to connect. Focus on teamwork and wellness — no income guarantees.";
      }
      return "Problem-solution reel: busy schedule, hard to eat enough fruits and vegetables. Position product as daily nutrition support. No medical claims.";
    },
    reelBriefHelp(): string {
      if (this.reelType === "business") {
        return "Describe your community angle and invitation. No income guarantees, earnings claims, or misleading opportunity promises.";
      }
      return "Use for reel angle or story structure (e.g. problem → daily habit → product, lifestyle pain like skipped meals). Do not name diseases, symptoms, or pain conditions; no cure, treatment, prevention, or “alternative to medicine” claims. Food supplements only — say “food supplement, not a medicine” in the post caption when relevant.";
    },
    reelScriptGenerateDisabled(): boolean {
      if (!this.hasGhlLocation || this.reelScriptLoading || this.reelGenerating) {
        return true;
      }
      if (this.reelType === "business") {
        return this.reelBrief.trim().length < 20;
      }
      if (this.reelType === "product") {
        return !this.reelProductSku;
      }
      return true;
    },
    reelGenerateDisabled(): boolean {
      return !this.hasGhlLocation
        || !this.reelVoiceover.trim()
        || this.reelScriptLoading
        || this.reelGenerating;
    },
    reelScenes: {
      get(): any[] {
        return Array.isArray(this.reelScriptJson?.scenes) ? this.reelScriptJson.scenes : [];
      },
      set(scenes: any[]) {
        const base = this.reelScriptJson && typeof this.reelScriptJson === "object"
          ? this.reelScriptJson
          : {};
        this.reelScriptJson = { ...base, scenes: Array.isArray(scenes) ? scenes : [] };
        this.persistReelSessionState();
      }
    },
    reelGenerateButtonLabel(): string {
      const voice = this.reelVoices.find((entry) => entry.id === this.reelVoiceId);
      const credits = voice?.estimatedCredits || 2;
      return `Generate video · ~${credits} credits · about 30 seconds`;
    },
    selectedReelVoiceLabel(): string {
      const voice = this.reelVoices.find((entry) => entry.id === this.reelVoiceId);
      return voice?.label || this.reelVoiceId || "Not selected";
    },
    selectedReelToneLabel(): string {
      const tone = this.reelTones.find((entry) => entry.id === this.reelToneId);
      return tone?.label || this.reelToneId || "Not selected";
    },
    reelPanelCollapsedSummary(): string {
      if (this.reelPanelExpanded || !this.hasReelComposerActivity()) {
        return "";
      }
      if (this.reelGenerating) {
        return "Generating video…";
      }
      if (this.reelScriptLoading) {
        return "Generating script…";
      }
      if (this.reelJobId) {
        return "Video in progress";
      }
      if (this.reelVoiceover.trim()) {
        return "Script ready";
      }
      return "Draft in progress";
    },
    postButtonLabel(): string {
      return "Post";
    },
    followUpCommentLength(): number {
      return this.form.followUpComment.length;
    },
    hasFacebookSelected(): boolean {
      return this.selectedAccounts.some(
        (account) => normalizePlatform(account.platform) === "facebook"
      );
    },
    facebookPostTypeHelp(): string {
      if (this.form.facebookPostType === "reel") {
        if (this.facelessReelEnabled) {
          return "Reels are short vertical videos. Auto-generate one in Faceless Reel below, or upload your own video before publishing.";
        }
        return "Reels are short vertical videos. Attach a video before publishing.";
      }
      if (this.form.facebookPostType === "story") {
        return "Stories disappear after 24 hours. Attach a video before publishing.";
      }
      return "Standard feed post with text, images, or links.";
    },
    previewHasVideo(): boolean {
      return hasPendingVideoFiles(this.pendingFiles)
        || contentHasVideo(this.previewMediaUrls);
    },
    requiresSingleVideo(): boolean {
      return facebookPostTypeRequiresVideo(this.form.facebookPostType);
    },
    facelessReelEnabled(): boolean {
      return isFacelessReelPreviewUser();
    }
  },
  async mounted() {
    const startupTasks = [this.loadProfile(), this.loadContentSettings()];
    if (this.facelessReelEnabled) {
      startupTasks.push(this.loadReelVoices(), this.loadReelCatalog());
    }
    await Promise.all(startupTasks);
    this.applyRoutePrefill();
    await this.loadDraftIfNeeded();
    this.resetFacelessReelGeneratorState();
    if (this.facelessReelEnabled) {
      await this.restoreReelSessionState();
    }
    this.loading = false;
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopReelPolling();
    this.stopVoicePreview();
    this.revokePendingPreviews();
  },
  watch: {
    "$route.query": {
      deep: true,
      async handler() {
        this.applyRoutePrefill();
        await this.loadDraftIfNeeded();
        this.resetFacelessReelGeneratorState();
        if (this.facelessReelEnabled) {
          await this.restoreReelSessionState();
        }
      }
    },
    campaignId: {
      immediate: false,
      async handler(value: number) {
        if (value) {
          await this.loadDraft(value);
        }
      }
    },
    attachedMediaIds: {
      deep: true,
      handler() {
        this.syncAttachedPreviewUrls();
      }
    },
    "form.facebookPostType"(value: string) {
      if (value !== "reel") {
        this.clearReelSessionState();
      } else if (this.facelessReelEnabled) {
        this.persistReelSessionState();
      }
    }
  },
  methods: {
    isCampaignStatusEditable(status: string) {
      return EDITABLE_CAMPAIGN_STATUSES.has(String(status || "").toLowerCase());
    },
    resolveWritableCampaignId() {
      if (!this.editingCampaignId) {
        return 0;
      }
      if (!this.isCampaignStatusEditable(this.campaignStatus)) {
        return 0;
      }
      return this.editingCampaignId;
    },
    detachLockedCampaign({ clearSession = true } = {}) {
      this.editingCampaignId = 0;
      if (clearSession) {
        this.clearReelSessionState();
      }
    },
    resetReelComposerFields() {
      this.stopReelPolling();
      this.reelGenerating = false;
      this.reelJobError = "";
      this.reelJobId = 0;
      this.reelScriptJson = null;
      this.reelBrief = "";
      this.reelVoiceover = "";
      this.reelType = "product";
      this.reelCommunityMediaIds = [];
      this.reelPanelExpanded = false;
    },
    hasReelComposerActivity() {
      return Boolean(
        this.reelGenerating
        || this.reelScriptLoading
        || this.reelJobId
        || this.reelBrief.trim()
        || this.reelVoiceover.trim()
        || this.reelScriptJson
      );
    },
    syncReelPanelExpandedFromActivity() {
      if (this.hasReelComposerActivity()) {
        this.reelPanelExpanded = true;
      }
    },
    clearPublishRouteCampaignId() {
      if (!this.embedded || !this.$route.query.campaignId) {
        return;
      }
      const query = { ...this.$route.query };
      delete query.campaignId;
      this.$router.replace({ query });
    },
    beginNewPost() {
      this.stopPolling();
      this.resetReelComposerFields();
      this.clearReelSessionState();
      this.revokePendingPreviews();
      this.detachLockedCampaign({ clearSession: true });
      this.campaignStatus = "";
      this.form.caption = "";
      this.form.linkUrl = "";
      this.form.followUpComment = "";
      this.form.scheduledAt = "";
      this.form.facebookPostType = "post";
      this.form.cascadeToTeam = false;
      this.form.varyCaption = false;
      this.mediaUrlsText = "";
      this.attachedMediaIds = [];
      this.attachedPreviewUrls = [];
      this.clearPublishRouteCampaignId();
      this.$emit("new-post");
    },
    releaseCampaignIfLocked(status: string) {
      if (this.isCampaignStatusEditable(status)) {
        return;
      }
      this.detachLockedCampaign();
      this.resetReelComposerFields();
      this.clearPublishRouteCampaignId();
    },
    resetFacelessReelGeneratorState() {
      if (this.facelessReelEnabled) {
        return;
      }
      this.stopReelPolling();
      this.reelGenerating = false;
      this.reelJobError = "";
      this.reelJobId = 0;
      this.reelScriptJson = null;
      this.reelBrief = "";
      this.reelVoiceover = "";
      this.reelProductSku = "";
      this.reelType = "product";
      this.clearReelSessionState();
    },
    getPlatformMeta,
    accountDisplayName,
    accountOptionKey,
    isPreviewImage(url: string) {
      return isImageMediaUrl(url);
    },
    async loadProfile() {
      const response = await settingsdata();
      this.isTeamLeader = Boolean(response.data?.isTeamLeader);
    },
    async loadContentSettings() {
      try {
        const response = await getContentSettings();
        const data = response.data || {};
        this.hasGhlLocation = Boolean(data.hasGhlLocation);
        this.ghlLocationId = String(data.locationId || "");
        const accounts = Array.isArray(data.socialAccounts) ? data.socialAccounts : [];
        this.connectedAccounts = accounts.filter(
          (account: any) => account.status !== "disconnected"
        );
      } catch {
        this.hasGhlLocation = false;
        this.ghlLocationId = "";
        this.connectedAccounts = [];
      }
    },
    onAccountsChange() {
      this.form.platforms = uniquePlatformsFromAccounts(this.selectedAccounts);
    },
    applyRoutePrefill() {
      const query = this.$route.query || {};
      if (query.caption) this.form.caption = String(query.caption);
      if (query.linkUrl) this.form.linkUrl = String(query.linkUrl);
      if (query.mediaUrl) this.mediaUrlsText = String(query.mediaUrl);
      if (query.name && !this.form.caption) {
        this.form.caption = String(query.name);
      }
    },
    resolveCampaignId() {
      if (this.campaignId) return this.campaignId;
      const queryId = Number(this.$route.query.campaignId || 0);
      return queryId || this.editingCampaignId;
    },
    async loadDraftIfNeeded() {
      let id = this.resolveCampaignId();
      if (!id) {
        try {
          const raw = sessionStorage.getItem(this.reelSessionStorageKey());
          const saved = raw ? JSON.parse(raw) : null;
          if (saved?.campaignId) {
            id = Number(saved.campaignId);
          }
        } catch {
          // ignore invalid session payload
        }
      }
      if (id) {
        await this.loadDraft(id);
        return;
      }
      this.detachLockedCampaign();
    },
    async loadDraft(id: number) {
      try {
        const response = await getContentCampaign(id);
        const campaign = response.data || response;
        if (!campaign?.id) return;

        const status = campaign.status || "";
        const openedFromRoute = Boolean(this.campaignId || Number(this.$route.query.campaignId || 0));

        if (!this.isCampaignStatusEditable(status)) {
          this.clearReelSessionState();
          this.detachLockedCampaign({ clearSession: false });
          if (openedFromRoute) {
            Swal.fire({
              title: "",
              text: "This campaign was already published and cannot be edited. Start a new post instead.",
              icon: "info",
              confirmButtonClass: "btn btn-secondary"
            });
          }
          return;
        }

        this.editingCampaignId = campaign.id;
        this.campaignStatus = status;
        const content = campaign.content || {};
        this.form.caption = content.caption || "";
        this.form.linkUrl = content.linkUrl || "";
        this.form.followUpComment = content.followUpComment || "";
        this.form.facebookPostType = content.facebookPostType || "post";
        this.mediaUrlsText = this.formatMediaUrlsForDraft(content.mediaUrls || []);
        this.attachedMediaIds = Array.isArray(content.mediaIds) ? [...content.mediaIds] : [];
        this.applyReelContentFields(content);
        this.resetFacelessReelGeneratorState();
        this.form.cascadeToTeam = Boolean(campaign.teamId);
        this.form.varyCaption = Boolean(content.varyCaption);
        if (campaign.scheduledAt) {
          const local = new Date(campaign.scheduledAt);
          const offset = local.getTimezoneOffset();
          const adjusted = new Date(local.getTime() - offset * 60000);
          this.form.scheduledAt = adjusted.toISOString().slice(0, 16);
        } else {
          this.form.scheduledAt = "";
        }

        this.syncAccountsFromPlatforms(campaign.platforms || []);
        await nextTick();
        this.syncAttachedPreviewUrls();
        this.pruneAttachedToSingleVideo();

        if (this.campaignStatus === "preparing") {
          this.startPolling(campaign.id);
        }

        if (this.facelessReelEnabled && this.reelJobId) {
          this.resumeReelJobPolling(this.reelJobId);
        }

        this.syncReelPanelExpandedFromActivity();
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to load draft.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      }
    },
    syncAccountsFromPlatforms(platforms: string[]) {
      const normalized = new Set((platforms || []).map((p) => String(p).toLowerCase()));
      this.selectedAccountKeys = this.connectedAccounts
        .filter((account) => normalized.has(String(account.platform || "").toLowerCase()))
        .map((account) => accountOptionKey(account));
      this.form.platforms = uniquePlatformsFromAccounts(this.selectedAccounts);
    },
    syncAttachedPreviewUrls() {
      const library = this.$refs.mediaLibrary as any;
      if (!library) return;
      this.attachedPreviewUrls = this.attachedMediaIds
        .map((id) => library.getPublicUrlForId(id))
        .filter(Boolean);
    },
    formatMediaUrlsForDraft(urls: string[]) {
      const normalized = facebookPostTypeRequiresVideo(this.form.facebookPostType)
        ? keepSingleVideoMediaUrl(urls)
        : urls.map((url) => String(url || "").trim()).filter(Boolean);
      return normalized.join("\n");
    },
    clearStoredVideoUrls() {
      const lines = this.mediaUrlsText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      this.mediaUrlsText = stripVideoMediaUrls(lines).join("\n");
    },
    pruneAttachedToSingleVideo() {
      if (!this.requiresSingleVideo) return;
      const videoIds = this.attachedMediaIds.filter((id) => !this.isLibraryImage(id));
      if (videoIds.length <= 1) return;
      this.keepOnlyImagesAndVideo(videoIds[videoIds.length - 1]);
      this.syncAttachedPreviewUrls();
    },
    onMediaLibraryRefresh() {
      this.syncAttachedPreviewUrls();
    },
    reelSessionStorageKey() {
      const instance = getActiveInstance() || "default";
      return `ghl:publish:reel:${instance}`;
    },
    applyReelContentFields(content: any = {}) {
      this.reelJobId = Number(content.reelJobId || 0);
      this.reelVoiceId = content.reelVoiceId || this.reelVoiceId;
      this.reelToneId = content.reelToneId || content.reelScript?.toneId || this.reelToneId;
      this.reelProductSku = content.reelProductSku || "";
      this.reelType = content.reelType || "product";
      this.reelBrief = content.reelBrief || "";
      this.reelCommunityMediaIds = Array.isArray(content.reelCommunityMediaIds)
        ? content.reelCommunityMediaIds.map((id: any) => Number(id)).filter((id: number) => id > 0)
        : [];
      if (content.reelScript) {
        this.reelScriptJson = content.reelScript;
        this.reelVoiceover = content.reelScript.voiceover || "";
        if (content.reelScript.caption && !this.form.caption.trim()) {
          this.form.caption = content.reelScript.caption;
        }
      } else if (content.facebookPostType !== "reel") {
        this.reelScriptJson = null;
        this.reelBrief = "";
        this.reelVoiceover = "";
        this.reelType = "product";
      }
    },
    persistReelSessionState() {
      if (!this.facelessReelEnabled) {
        return;
      }
      if (this.form.facebookPostType !== "reel") {
        this.clearReelSessionState();
        return;
      }
      if (!this.reelVoiceover.trim() && !this.reelBrief.trim() && !this.reelJobId) {
        return;
      }

      const payload = {
        campaignId: this.resolveWritableCampaignId() || 0,
        facebookPostType: this.form.facebookPostType,
        selectedAccountKeys: [...this.selectedAccountKeys],
        reelJobId: this.reelJobId || 0,
        reelVoiceId: this.reelVoiceId,
        reelToneId: this.reelToneId,
        reelProductSku: this.reelProductSku,
        reelType: this.reelType,
        reelBrief: this.reelBrief,
        reelCommunityMediaIds: [...this.reelCommunityMediaIds],
        reelVoiceover: this.reelVoiceover,
        reelScriptJson: this.reelScriptJson,
        reelGenerating: this.reelGenerating
      };

      try {
        sessionStorage.setItem(this.reelSessionStorageKey(), JSON.stringify(payload));
      } catch {
        // ignore quota / private mode errors
      }
    },
    clearReelSessionState() {
      try {
        sessionStorage.removeItem(this.reelSessionStorageKey());
      } catch {
        // ignore
      }
    },
    async restoreReelSessionState() {
      if (!this.facelessReelEnabled) {
        return;
      }
      let saved: any = null;
      try {
        const raw = sessionStorage.getItem(this.reelSessionStorageKey());
        saved = raw ? JSON.parse(raw) : null;
      } catch {
        saved = null;
      }
      if (!saved || saved.facebookPostType !== "reel") {
        return;
      }

      const draftId = this.editingCampaignId || 0;
      if (saved.campaignId && draftId && Number(saved.campaignId) !== Number(draftId)) {
        return;
      }
      if (saved.campaignId && !draftId) {
        // Session points at a campaign we did not load (likely already published).
        this.clearReelSessionState();
        return;
      }

      this.form.facebookPostType = "reel";
      if (Array.isArray(saved.selectedAccountKeys) && saved.selectedAccountKeys.length) {
        const validKeys = new Set(this.connectedAccounts.map((account: any) => accountOptionKey(account)));
        this.selectedAccountKeys = saved.selectedAccountKeys.filter((key: string) => validKeys.has(key));
        this.onAccountsChange();
      }

      if (saved.reelVoiceId) this.reelVoiceId = saved.reelVoiceId;
      if (saved.reelToneId) this.reelToneId = saved.reelToneId;
      if (saved.reelProductSku) this.reelProductSku = saved.reelProductSku;
      if (saved.reelType) this.reelType = saved.reelType;
      if (saved.reelBrief) this.reelBrief = saved.reelBrief;
      if (Array.isArray(saved.reelCommunityMediaIds)) {
        this.reelCommunityMediaIds = saved.reelCommunityMediaIds
          .map((id: any) => Number(id))
          .filter((id: number) => id > 0);
      }
      if (saved.reelVoiceover) this.reelVoiceover = saved.reelVoiceover;
      if (saved.reelCaptionDraft && !this.form.caption.trim()) {
        this.form.caption = saved.reelCaptionDraft;
      }
      if (saved.reelScriptJson) this.reelScriptJson = saved.reelScriptJson;
      if (!saved.reelToneId && saved.reelScriptJson?.toneId) {
        this.reelToneId = saved.reelScriptJson.toneId;
      }

      const sessionJobId = Number(saved.reelJobId || 0);
      if (sessionJobId && (!this.reelJobId || this.reelJobId === sessionJobId)) {
        this.reelJobId = sessionJobId;
      }

      if (this.reelJobId) {
        if (saved.reelGenerating) {
          this.reelGenerating = true;
        }
        this.resumeReelJobPolling(this.reelJobId);
      }

      this.syncReelPanelExpandedFromActivity();
    },
    async persistReelDraftSilent() {
      if (!this.facelessReelEnabled) return;
      if (!this.hasGhlLocation || !this.selectedAccountKeys.length) return;
      if (this.form.facebookPostType !== "reel") return;
      if (!this.reelVoiceover.trim() && !this.reelBrief.trim() && !this.reelJobId) return;

      const payload = this.buildPayload({ publishNow: false });
      if (!payload.content.caption?.trim()) {
        payload.content.caption = (
          this.reelVoiceover.slice(0, 500)
          || "Reel draft"
        ).trim();
      }

      try {
        const files = this.pendingFiles.map((item) => item.file);
        let id = this.resolveWritableCampaignId();
        let response;
        try {
          response = id
            ? await updateContentCampaign(id, payload, files)
            : await createContentCampaign(payload, files);
        } catch (e) {
          const message = getApiErrorMessage(e);
          if (id && message.includes("Only draft, preparing, or rejected campaigns can be edited")) {
            this.releaseCampaignIfLocked(this.campaignStatus);
            response = await createContentCampaign(payload, files);
          } else {
            throw e;
          }
        }
        const campaign = response.data?.campaign || response.data || {};
        if (campaign.id) {
          this.editingCampaignId = campaign.id;
          this.campaignStatus = campaign.status || this.campaignStatus || "draft";
        }
        this.persistReelSessionState();
      } catch {
        this.persistReelSessionState();
      }
    },
    async loadReelVoices() {
      try {
        const response = await getReelVoices();
        const voices = Array.isArray(response.data?.voices) ? response.data.voices : [];
        const tones = Array.isArray(response.data?.tones) ? response.data.tones : [];
        this.reelVoices = voices;
        if (tones.length) this.reelTones = tones;
        if (response.data?.previewBase) {
          this.reelPreviewBase = String(response.data.previewBase).replace(/\/$/, "");
        } else if (voices[0]?.previewUrl) {
          this.reelPreviewBase = String(voices[0].previewUrl).replace(/\/[^/]+$/, "");
        }
        if (voices.length && !voices.some((voice) => voice.id === this.reelVoiceId)) {
          this.reelVoiceId = voices[0].id;
        }
        if (this.reelTones.length && !this.reelTones.some((tone) => tone.id === this.reelToneId)) {
          this.reelToneId = this.reelTones[0].id;
        }
      } catch {
        this.reelVoices = [];
      }
    },
    async loadReelCatalog() {
      this.reelCatalogLoading = true;
      try {
        const response = await fetchGhlPaymentCatalogList();
        const products = Array.isArray(response.data?.products) ? response.data.products : [];
        this.reelCatalogProducts = filterReelEligibleProducts(products);
        if (
          this.reelProductSku
          && !this.reelCatalogProducts.some((product) => product.sku === this.reelProductSku)
        ) {
          this.reelProductSku = "";
        }
      } catch {
        this.reelCatalogProducts = [];
      } finally {
        this.reelCatalogLoading = false;
      }
    },
    stopVoicePreview() {
      if (this.reelPreviewAudio) {
        this.reelPreviewAudio.pause();
        this.reelPreviewAudio = null;
      }
    },
    playVoicePreview(voice: any, toneId?: string) {
      const selectedVoice = voice || this.reelVoices.find((entry) => entry.id === this.reelVoiceId);
      const voiceId = String(selectedVoice?.id || this.reelVoiceId || "").trim();
      const resolvedTone = String(toneId || this.reelToneId || "educator").trim();
      const voicePreviewUrl = String(selectedVoice?.previewUrl || "").trim();
      const usesHostedReelPreview = voicePreviewUrl.includes("/reel-voices/v");
      const url = voicePreviewUrl && /^https?:\/\//i.test(voicePreviewUrl) && !usesHostedReelPreview
        ? voicePreviewUrl
        : (() => {
          const base = voicePreviewUrl
            ? voicePreviewUrl.replace(/\/[^/]+\.mp3$/i, "")
            : String(this.reelPreviewBase || "").replace(/\/$/, "");
          return voiceId && base
            ? `${base}/${voiceId}-${resolvedTone}.mp3`
            : voicePreviewUrl;
        })();
      if (!url) return;
      this.stopVoicePreview();
      const audio = new Audio(url);
      this.reelPreviewAudio = audio;
      audio.play().catch(() => {
        Swal.fire({
          icon: "info",
          title: "Preview unavailable",
          text: "Voice preview could not be loaded.",
          confirmButtonText: "OK"
        });
      });
    },
    buildReelScriptPayload() {
      const base = this.reelScriptJson && typeof this.reelScriptJson === "object"
        ? { ...this.reelScriptJson }
        : { scenes: [] };
      const isBusinessReel = this.reelType === "business";
      return {
        ...base,
        voiceover: this.reelVoiceover.trim(),
        caption: (this.form.caption || this.reelVoiceover).trim(),
        toneId: this.reelToneId,
        reelType: this.reelType,
        product: isBusinessReel
          ? null
          : (base.product || this.reelCatalogProducts.find((p) => p.sku === this.reelProductSku) || null)
      };
    },
    async generateReelScript() {
      if (this.reelScriptGenerateDisabled) return;
      this.reelScriptLoading = true;
      this.reelJobError = "";
      try {
        const payload: any = {
          voiceId: this.reelVoiceId,
          toneId: this.reelToneId
        };
        if (this.reelType) payload.reelType = this.reelType;
        if (this.reelType === "product" && this.reelProductSku) {
          payload.productId = this.reelProductSku;
        }
        if (this.reelBrief.trim()) payload.brief = this.reelBrief.trim();
        const response = await generateReelScript(payload);
        const script = response.data?.script || {};
        this.reelScriptJson = script;
        this.reelVoiceover = script.voiceover || "";
        if (script.caption && !this.form.caption.trim()) {
          this.form.caption = script.caption;
        }
        if (response.data?.voiceId) {
          this.reelVoiceId = response.data.voiceId;
        }
        if (response.data?.toneId || script.toneId) {
          this.reelToneId = response.data?.toneId || script.toneId;
        }
        this.persistReelSessionState();
        await this.persistReelDraftSilent();
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to generate Reel script.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.reelScriptLoading = false;
      }
    },
    async generateReelVideo() {
      if (this.reelGenerateDisabled) return;
      this.reelGenerating = true;
      this.reelJobError = "";
      try {
        const script = this.buildReelScriptPayload();
        const jobPayload: any = {
          voiceId: this.reelVoiceId,
          toneId: this.reelToneId,
          locationId: this.ghlLocationId || undefined,
          campaignId: this.editingCampaignId || undefined,
          reelType: this.reelType,
          script
        };
        if (this.reelCommunityMediaIds.length) {
          jobPayload.communityMediaIds = [...this.reelCommunityMediaIds];
        }
        const response = await createReelJob(jobPayload);
        this.reelJobId = Number(response.data?.jobId || 0);
        this.reelScriptJson = script;
        this.persistReelSessionState();
        await this.persistReelDraftSilent();
        this.startReelPolling(this.reelJobId);
      } catch (e) {
        this.reelGenerating = false;
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to start Reel video generation.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      }
    },
    startReelPolling(jobId: number) {
      this.stopReelPolling();
      if (!jobId) return;
      this.reelPollTimer = setInterval(() => {
        this.pollReelJob(jobId);
      }, 3000);
      this.pollReelJob(jobId);
    },
    resumeReelJobPolling(jobId: number) {
      getReelJob(jobId)
        .then((response) => {
          const job = response.data || {};
          if (job.status === "ready" && job.publicUrl) {
            this.attachReelVideoFromJob(job);
            this.reelGenerating = false;
            this.persistReelSessionState();
            return;
          }
          if (job.status === "failed") {
            this.reelJobError = job.error || "Reel generation failed.";
            this.reelGenerating = false;
            this.persistReelSessionState();
            return;
          }
          if (job.status === "queued" || job.status === "running") {
            this.reelGenerating = true;
            this.persistReelSessionState();
            this.startReelPolling(jobId);
          }
        })
        .catch(() => {
          this.reelJobError = "Unable to resume Reel job.";
        });
    },
    stopReelPolling() {
      if (this.reelPollTimer) {
        clearInterval(this.reelPollTimer);
        this.reelPollTimer = null;
      }
    },
    async pollReelJob(jobId: number) {
      try {
        const response = await getReelJob(jobId);
        const job = response.data || {};
        if (job.status === "ready") {
          this.stopReelPolling();
          this.reelGenerating = false;
          this.reelJobError = "";
          await this.attachReelVideoFromJob(job);
          this.persistReelSessionState();
          await this.persistReelDraftSilent();
          return;
        }
        if (job.status === "failed") {
          this.stopReelPolling();
          this.reelGenerating = false;
          this.reelJobError = job.error || "Reel generation failed.";
          this.persistReelSessionState();
        }
      } catch {
        // keep polling
      }
    },
    isLibraryImage(id: number) {
      const library = this.$refs.mediaLibrary as any;
      const file = library?.getFileForId?.(id);
      if (file) {
        return Boolean(library.isImage?.(file));
      }
      const url = String(library?.getPublicUrlForId?.(id) || "").trim();
      return Boolean(url) && isImageMediaUrl(url);
    },
    clearPendingVideos() {
      const kept: PendingFile[] = [];
      for (const item of this.pendingFiles) {
        if (item.isImage) {
          kept.push(item);
          continue;
        }
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
      this.pendingFiles = kept;
    },
    keepOnlyImagesAndVideo(mediaId: number) {
      const imageIds = this.attachedMediaIds.filter((id) => (
        id !== mediaId && this.isLibraryImage(id)
      ));
      this.attachedMediaIds = mediaId ? [...imageIds, mediaId] : imageIds;
    },
    async attachReelVideoFromJob(job: any) {
      const publicUrl = String(job?.publicUrl || "").trim();
      const mediaId = Number(job?.mediaId || 0);
      this.clearPendingVideos();
      this.clearStoredVideoUrls();
      if (mediaId) {
        this.keepOnlyImagesAndVideo(mediaId);
        await nextTick();
        this.syncAttachedPreviewUrls();
        const library = this.$refs.mediaLibrary as any;
        await library?.loadMedia?.();
        this.keepOnlyImagesAndVideo(mediaId);
        this.syncAttachedPreviewUrls();
        return;
      }
      if (!publicUrl) return;

      try {
        const fetched = await fetch(publicUrl);
        const blob = await fetched.blob();
        const file = new File([blob], `faceless-reel-${job.jobId || Date.now()}.mp4`, {
          type: blob.type || "video/mp4"
        });
        this.keepOnlyImagesAndVideo(0);
        this.pendingFiles.push({
          file,
          previewUrl: URL.createObjectURL(file),
          isImage: false
        });
      } catch {
        this.reelJobError = "Video is ready but could not be attached automatically.";
      }
    },
    viewHistory() {
      if (this.embedded) {
        this.$emit("view-history");
        return;
      }
      this.$router.push({ path: "/settings/ai247-portal", query: { tab: "history" } });
    },
    openSuggestCaption(mode: "draft" | "rewrite") {
      if (!this.hasGhlLocation) return;
      if (!this.selectedAccountKeys.length) {
        Swal.fire({
          icon: "info",
          title: "Select accounts first",
          text: "Choose at least one social account under Post to before suggesting a caption.",
          confirmButtonText: "OK"
        });
        return;
      }
      this.suggestCaptionMode = mode;
      this.suggestCaptionVisible = true;
    },
    openSuggestPostImage() {
      if (!this.hasGhlLocation) return;
      if (!this.selectedAccountKeys.length) {
        Swal.fire({
          icon: "info",
          title: "Select accounts first",
          text: "Choose at least one social account under Post to before suggesting an image.",
          confirmButtonText: "OK"
        });
        return;
      }
      this.suggestPostImageVisible = true;
    },
    applySuggestedImage(payload: { b64Json?: string; mimeType?: string }) {
      const b64Json = String(payload?.b64Json || "").trim();
      const mimeType = String(payload?.mimeType || "image/png");
      if (!b64Json) return;

      const binary = atob(b64Json);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const extension = mimeType.includes("jpeg") ? "jpg" : "png";
      const file = new File([bytes], `suggest-post-image.${extension}`, { type: mimeType });
      this.pendingFiles.push({
        file,
        previewUrl: URL.createObjectURL(file),
        isImage: true
      });
    },
    applySuggestedCaption(text: string) {
      const nextCaption = String(text || "").trim();
      if (!nextCaption) return;
      if (
        this.suggestCaptionMode !== "rewrite"
        && this.form.caption.trim()
        && this.form.caption.trim() !== nextCaption
      ) {
        Swal.fire({
          icon: "question",
          title: "Replace caption?",
          text: "This will replace the text currently in your post.",
          showCancelButton: true,
          confirmButtonText: "Replace",
          cancelButtonText: "Keep current"
        }).then((result) => {
          if (result.isConfirmed) {
            this.form.caption = nextCaption;
          }
        });
        return;
      }
      this.form.caption = nextCaption;
    },
    triggerFilePicker() {
      const input = this.$refs.fileInput as HTMLInputElement;
      input?.click();
    },
    onFilesSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const files = Array.from(input.files || []);
      for (const file of files) {
        const isImage = file.type.startsWith("image/");
        if (!isImage && this.requiresSingleVideo) {
          this.clearPendingVideos();
          this.keepOnlyImagesAndVideo(0);
        }
        this.pendingFiles.push({
          file,
          previewUrl: URL.createObjectURL(file),
          isImage
        });
        if (!isImage && this.requiresSingleVideo) {
          break;
        }
      }
      input.value = "";
    },
    removePendingFile(index: number) {
      const item = this.pendingFiles[index];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      this.pendingFiles.splice(index, 1);
    },
    revokePendingPreviews() {
      for (const item of this.pendingFiles) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
      this.pendingFiles = [];
    },
    buildPayload({ publishNow = true } = {}) {
      const mediaUrls = facebookPostTypeRequiresVideo(this.form.facebookPostType)
        ? keepSingleVideoMediaUrl(this.mediaUrls)
        : this.mediaUrls;
      const payload: any = {
        content: {
          caption: this.form.caption.trim(),
          mediaUrls,
          linkUrl: this.form.linkUrl.trim() || undefined
        },
        existingMediaIds: this.attachedMediaIds,
        platforms: this.form.platforms,
        scheduledAt: this.form.scheduledAt ? new Date(this.form.scheduledAt).toISOString() : undefined,
        publishNow,
        cascadeToTeam: Boolean(this.form.cascadeToTeam)
      };

      const followUpComment = this.form.followUpComment.trim();
      if (followUpComment) {
        payload.content.followUpComment = followUpComment;
      }
      if (this.hasFacebookSelected) {
        payload.content.facebookPostType = this.form.facebookPostType;
      }
      if (this.form.cascadeToTeam && this.form.varyCaption) {
        payload.content.varyCaption = true;
      }
      if (this.form.facebookPostType === "reel") {
        if (this.reelJobId) payload.content.reelJobId = this.reelJobId;
        if (this.reelVoiceId) payload.content.reelVoiceId = this.reelVoiceId;
        if (this.reelToneId) payload.content.reelToneId = this.reelToneId;
        if (this.reelProductSku) payload.content.reelProductSku = this.reelProductSku;
        if (this.reelType) payload.content.reelType = this.reelType;
        if (this.reelBrief.trim()) payload.content.reelBrief = this.reelBrief.trim();
        if (this.reelCommunityMediaIds.length) {
          payload.content.reelCommunityMediaIds = [...this.reelCommunityMediaIds];
        }
        if (this.reelScriptJson || this.reelVoiceover.trim()) {
          payload.content.reelScript = this.buildReelScriptPayload();
        }
      }

      return payload;
    },
    validateComposer() {
      if (!this.hasGhlLocation) {
        Swal.fire({
          title: "",
          text: "Connect your AI 24/7 before publishing content.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (!this.selectedAccountKeys.length) {
        Swal.fire({
          title: "",
          text: "Select at least one social account.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      const hasMedia = this.mediaUrls.length > 0
        || this.pendingFiles.length > 0
        || this.attachedMediaIds.length > 0;
      if (!this.form.caption.trim() && !hasMedia) {
        Swal.fire({
          title: "",
          text: "Caption or media is required.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (!this.form.platforms.length) {
        Swal.fire({
          title: "",
          text: "Select at least one social account.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (this.form.followUpComment.length > this.maxFollowUpCommentLength) {
        Swal.fire({
          title: "",
          text: `Follow-up comment must be at most ${this.maxFollowUpCommentLength} characters.`,
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (
        this.hasFacebookSelected
        && facebookPostTypeRequiresVideo(this.form.facebookPostType)
        && !this.previewHasVideo
      ) {
        Swal.fire({
          title: "",
          text: "Facebook Reels and Stories require a video attachment.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      return true;
    },
    async submitCampaign({ publishNow = true } = {}) {
      const payload = this.buildPayload({ publishNow });
      const files = this.pendingFiles.map((item) => item.file);
      let id = this.resolveWritableCampaignId();

      if (id) {
        try {
          return await updateContentCampaign(id, payload, files);
        } catch (e) {
          const message = getApiErrorMessage(e);
          if (message.includes("Only draft, preparing, or rejected campaigns can be edited")) {
            this.releaseCampaignIfLocked(this.campaignStatus);
            id = 0;
          } else {
            throw e;
          }
        }
      }
      return createContentCampaign(payload, files);
    },
    handleCampaignResponse(response: any, { publishNow = true } = {}) {
      const data = response.data || {};
      const campaign = data.campaign || {};
      const status = campaign.status || (publishNow ? "queued" : "draft");

      this.editingCampaignId = campaign.id || this.editingCampaignId;
      this.campaignStatus = status;
      this.releaseCampaignIfLocked(status);
      const moderation = data.moderation || campaign.moderationResult || {};
      this.lastResult = {
        campaignId: campaign.id,
        status,
        postedCount: data.postedCount || 0,
        skippedCount: data.skippedCount || 0,
        failedCount: data.failedCount || 0,
        moderationSummary: moderation.summary || ""
      };

      this.revokePendingPreviews();

      if (status === "preparing" || status === "pending_review" || status === "awaiting_original") {
        this.startPolling(campaign.id);
      } else {
        this.stopPolling();
      }

      const library = this.$refs.mediaLibrary as any;
      library?.loadMedia?.();

      return { data, status };
    },
    startPolling(campaignId: number) {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        try {
          const response = await getContentCampaign(campaignId);
          const campaign = response.data || response;
          this.campaignStatus = campaign.status || "";

          if (campaign.status !== "preparing"
            && campaign.status !== "pending_review"
            && campaign.status !== "awaiting_original") {
            this.stopPolling();
            this.releaseCampaignIfLocked(campaign.status || "");
            const content = campaign.content || {};
            this.mediaUrlsText = (content.mediaUrls || []).join("\n");
            this.attachedMediaIds = [];
            this.syncAttachedPreviewUrls();
            if (this.lastResult?.campaignId === campaign.id) {
              this.lastResult = {
                ...this.lastResult,
                status: campaign.status || this.lastResult.status,
                moderationSummary: campaign.moderationResult?.summary || this.lastResult.moderationSummary || "",
                postedCount: campaign.stats?.posted ?? this.lastResult.postedCount,
                skippedCount: campaign.stats?.skipped ?? this.lastResult.skippedCount,
                failedCount: campaign.stats?.failed ?? this.lastResult.failedCount
              };
            }
          }
        } catch {
          this.stopPolling();
        }
      }, 3000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async saveDraft() {
      if (!this.validateComposer()) return;

      this.saving = true;
      try {
        const response = await this.submitCampaign({ publishNow: false });
        const { status } = this.handleCampaignResponse(response, { publishNow: false });
        const message = status === "preparing"
          ? "Draft saved. Media is uploading in the background."
          : "Draft saved for later.";
        Swal.fire({
          title: "",
          text: message,
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to save draft.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.saving = false;
      }
    },
    handlePostMenu(command: string) {
      if (command === "now") {
        this.form.scheduledAt = "";
        this.submitPostNow();
        return;
      }
      if (command === "schedule") {
        this.scheduleDialogVisible = true;
      }
    },
    async submitPostNow() {
      this.form.scheduledAt = "";
      await this.submitPublish(true);
    },
    async submitScheduledPost() {
      if (!this.form.scheduledAt) {
        Swal.fire({
          title: "",
          text: "Choose a schedule date and time.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return;
      }
      this.scheduleDialogVisible = false;
      await this.submitPublish(true);
    },
    async submitPublish(publishNow = true) {
      if (!this.validateComposer()) return;

      this.publishing = true;
      try {
        const response = await this.submitCampaign({ publishNow });
        const { data, status } = this.handleCampaignResponse(response, { publishNow });

        let message = "Draft saved for later.";
        let icon: "success" | "warning" | "error" = "success";
        if (status === "preparing") {
          message = "Post queued. Media is uploading in the background.";
        } else if (status === "rejected") {
          const moderation = data.moderation || data.campaign?.moderationResult || {};
          message = moderation.summary
            ? `Post blocked by AI review: ${moderation.summary}`
            : "Post blocked by AI review against company rules.";
          icon = "error";
        } else if (status === "pending_review") {
          message = "Post is under AI review before team fan-out.";
          icon = "warning";
        } else if (publishNow) {
          message = `Published to ${data.postedCount} account(s). Skipped ${data.skippedCount}. Failed ${data.failedCount}.`;
          icon = data.failedCount ? "warning" : "success";
        }

        Swal.fire({
          title: "",
          text: message,
          icon,
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Publish failed.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.publishing = false;
      }
    }
  }
});
